import {
  AgencySubscriptionPricingOptionsResponse,
  AgencyTeamInfoResponse,
  AgencyUpdateResponse,
  AgentCompletedTasksResponse,
  AgentCreateCustomTemplateResponse,
  AgentCustomTemplateResponse,
  AgentEmailSentResponse,
  AgentEmailTemplateOptionsResponse,
  AgentEmailTemplateResponse,
  AgentIncentiveInfoResponse,
  AgentInfoResponse,
  AgentInfoWithAgencyResponse,
  AgentPublicSettings,
  Client,
  ClientEmailDetails,
  ClientListResponse,
  ClientNotificationResponse,
  ClientSmsDetails,
  ConstituentListResponse,
  ContactMedium,
  CustomEmailTemplatePreviewDetails,
  CustomLink,
  CustomSmsTemplatePreviewDetails,
  CustomTemplate,
  CustomTemplateDetails,
  EmailPreviewResponse,
  ImpersonationUser,
  Invoice,
  LineType,
  LoginData,
  LoginResponse,
  OwlResponse,
  PagedOwlResponse,
  PolicyChangeVersion,
  PublicSetting,
  RenewalFrequency,
  SMSPreviewResponse,
  SalesFunnelStatus,
  SalesFunnelStatusUpdateResponse,
  SendSMSResponse,
  SetPasswordDetails,
  StripePaymentIntentResponse,
  SubscriptionDetailsResponse,
  SubscriptionStatusResponse,
  UserResponse,
  ValidateTokenData,
  CustomLinkStats,
  SeatsInfoResponse,
  SeatsPricingInfoResponse,
  CreatedPolicyComparisonApiKey,
  PolicyComparisonApiKey,
} from '../../types';
import {
  AgencyInput,
  AgentInput,
  PolicyTypeResponse,
  ReferralPartnerInput,
  UpdateClientPolicyPayload,
} from '../../types/owl';
import { AbstractClient } from './AbstractClient';
import { ACCESS_TOKEN_KEY } from './constants';

export class AuthClient extends AbstractClient {
  getApiKeys = this.createGetHandler<
    PolicyComparisonApiKey[],
    Record<any, never>
  >('/me/api-keys');

  createApiKey = this.createPayloadHandler<
    CreatedPolicyComparisonApiKey,
    { payload: { name: string } }
  >('POST', '/me/api-keys');

  deleteApiKey = this.createPayloadHandler<
    void,
    { args: { apiKeyId: number } }
  >('DELETE', ({ apiKeyId }) => `/me/api-keys/${apiKeyId}`);

  sendMagicLink = this.createPayloadHandler<
    { data: null },
    { payload: { email: string } }
  >('POST', '/auth/login-magic-link');

  sendPasswordResetEmail = this.createPayloadHandler<
    //TODO wait until owl response can be changed up
    { data: null },
    { payload: { email: string } }
  >('POST', '/auth/send-reset-password-email');

  resetPassword = this.createPayloadHandler<
    OwlResponse<undefined>,
    {
      payload: SetPasswordDetails;
    }
  >('POST', '/auth/reset-password');

  sendClientEmailReminder = this.createPayloadHandler<
    ClientNotificationResponse,
    { args: { clientId: string } }
  >('POST', ({ clientId }) => `/client/${clientId}/send-reminder-email`);

  sendSMSReminder = this.createPayloadHandler<
    SendSMSResponse,
    { args: { clientId: string }; payload: { mobilePhoneNumber: string } }
  >('POST', ({ clientId }) => `/client/${clientId}/send-reminder-sms`);

  sendSubmissionEmailReminder = this.createPayloadHandler<
    ClientNotificationResponse,
    {
      args: { clientId: string; submissionId: string };
    }
  >(
    'POST',
    ({ clientId, submissionId }) =>
      `/client/${clientId}/submission/${submissionId}/send-reminder-email`,
  );

  getEmailReminderPreview = this.createGetHandler<
    EmailPreviewResponse,
    { args: { clientId: string } }
  >(({ clientId }) => `/client/${clientId}/preview-reminder-email`);

  getSMSReminderPreview = this.createGetHandler<
    SMSPreviewResponse,
    { args: { clientId: string } }
  >(({ clientId }) => `/client/${clientId}/preview-reminder-sms`);

  getEmailResubmitPreview = this.createGetHandler<
    EmailPreviewResponse,
    { args: { clientId: string } }
  >(({ clientId }) => `/client/${clientId}/preview-resubmit-email`);

  getSMSResubmitPreview = this.createGetHandler<
    SMSPreviewResponse,
    { args: { clientId: string } }
  >(({ clientId }) => `/client/${clientId}/preview-resubmit-sms`);

  sendResubmitEmail = this.createPayloadHandler<
    ClientNotificationResponse,
    { args: { clientId: string }; payload: { email: string; ccAgent: boolean } }
  >('POST', ({ clientId }) => `/client/${clientId}/send-resubmit-email`);

  sendResubmitSMS = this.createPayloadHandler<
    SendSMSResponse,
    {
      args: { clientId: string };
      payload: { mobilePhoneNumber: string; ccAgent: boolean };
    }
  >('POST', ({ clientId }) => `/client/${clientId}/send-resubmit-sms`);

  reassignClient = this.createPayloadHandler<
    void,
    {
      args: { clientId: string };
      payload: { newAgentId: number };
    }
  >('POST', ({ clientId }) => `/client/${clientId}/reassign`);

  getEmailReminderSubmissionPreview = this.createGetHandler<
    EmailPreviewResponse,
    { args: { clientId: string; submissionId: string } }
  >(
    ({ clientId, submissionId }) =>
      `/client/${clientId}/submission/${submissionId}/preview-reminder-email`,
  );

  changePassword = this.createPayloadHandler<
    { data: undefined },
    {
      payload: {
        current_password: string;
        password: string;
        password_confirmation: string;
      };
    }
  >('POST', '/auth/change-password');

  setNewPasswordAndLogin = this.createPayloadHandler<
    OwlResponse<any>,
    { payload: SetPasswordDetails }
  >('POST', '/auth/reset-password-and-login');

  updateAgency = this.createPayloadHandler<
    AgencyUpdateResponse,
    {
      args: { agencyId: number };
      payload: AgencyInput;
    }
  >('POST', ({ agencyId }) => `agency/${agencyId}/update-settings`, {
    isFormData: true,
  });

  batchArchiveClients = this.createPayloadHandler<
    OwlResponse<Omit<Client, 'drivers'>[]>,
    { payload: { clients: string[] } }
  >('POST', '/client/batch-archive');

  batchUnarchiveClients = this.createPayloadHandler<
    OwlResponse<Omit<Client, 'drivers'>[]>,
    { payload: { clients: string[] } }
  >('POST', '/client/batch-unarchive');

  fetchSubscriptionDetails = this.createGetHandler<
    SubscriptionDetailsResponse,
    {
      args: { agencyId: number };
    }
  >(({ agencyId }) => `agency/${agencyId}/subscription-details`);

  fetchSubscriptionStatus = this.createGetHandler<
    SubscriptionStatusResponse,
    {
      args: { agencyId: number };
    }
  >(({ agencyId }) => `agency/${agencyId}/subscription-status`);

  initiatePaymentMethodUpdate = this.createPayloadHandler<
    StripePaymentIntentResponse,
    { args: { agencyId: number } }
  >(
    'POST',
    ({ agencyId }) => `agency/${agencyId}/initiate-update-of-payment-method`,
  );

  fetchAgencyInvoices = this.createGetHandler<
    PagedOwlResponse<Invoice[]>,
    {
      args: { agencyId: number };
      query: { page: number; per_page?: number };
    }
  >(({ agencyId }) => `agency/${agencyId}/invoices`);

  //TODO move into separate client
  fetchClients = this.createGetHandler<
    ClientListResponse,
    {
      query: {
        page: number;
        'filters[]': string;
        search: string;
        includeArchived?: 1;
        insuranceLines?: LineType[];
        salesFunnelStatusFilters?: (keyof SalesFunnelStatus)[];
      };
    }
  >('/client', { include: '*' });

  fetchClientsV2 = this.createGetHandler<
    ClientListResponse,
    {
      args: { agentId: number };
      query: {
        page: number;
        'filters[]': string;
        search: string;
        includeArchived?: 1;
        insuranceLines?: LineType[];
        salesFunnelStatusFilters?: (keyof SalesFunnelStatus)[];
        attributionSource?: string;
      };
    }
  >(({ agentId }) => `/agent/${agentId}/clients`, {
    include:
      'clientSubmissions,clientSubmissions.carrier,clientSubmissions.declarationPages,clientSubmissions.documents,agentInitiatedEmail.tracking,agentInitiatedSms.tracking',
  });

  fetchClientDetail = this.createGetHandler<
    OwlResponse<Client>,
    {
      args: {
        clientId: string;
      };
    }
  >(({ clientId }) => `client/${clientId}/detail`, { include: '*' });

  getPolicyChanges = this.createGetHandler<
    OwlResponse<PolicyChangeVersion[]>,
    {
      args: { policyUuid: string };
    }
  >(({ policyUuid }) => `client-submission-policies/${policyUuid}/changes`);

  fetchFreeTrialClients = this.createGetHandler<
    OwlResponse<Client[]>,
    {
      args: { agentId: number };
    }
  >(({ agentId }) => `/agent/${agentId}/free-trial-clients`);

  fetchAgents = this.createGetHandler<
    AgencyTeamInfoResponse,
    {
      args: { agencyId: number };
      query: {
        includeInactive?: 1;
        page: number;
      };
    }
  >(({ agencyId }) => `/agency/${agencyId}/agents`, {
    include: '*',
    perPage: 9,
    includeInactive: 1,
  });

  fetchActiveAgents = this.createGetHandler<
    AgencyTeamInfoResponse,
    {
      args: { agencyId: number };
      query?: { perPage?: number };
    }
  >(({ agencyId }) => `/agency/${agencyId}/agents`, {
    perPage: 10,
  });

  fetchAgentConstituents = this.createGetHandler<
    ConstituentListResponse,
    {
      args: {
        agentId: number;
      };
      query: {
        page: number;
      };
    }
  >(({ agentId }) => `agent/${agentId}/constituents`);

  fetchCompletedTasks = this.createGetHandler<
    AgentCompletedTasksResponse,
    { args: { agentId: number } }
  >(({ agentId }) => `/agent/${agentId}/completed-learn-tasks`);

  updateCompletedTasks = this.createPayloadHandler<
    AgentCompletedTasksResponse,
    {
      args: { agentId: number };
      payload: { completedLearnTasks: string[] };
    }
  >('POST', ({ agentId }) => `/agent/${agentId}/update-completed-learn-tasks`);

  deactivateAgent = this.createPayloadHandler<
    AgentInfoResponse,
    {
      args: { agencyId: number; agentId: number };
      payload?: { remove_seat?: boolean };
    }
  >(
    'POST',
    ({ agencyId, agentId }) =>
      `/agency/${agencyId}/agent/${agentId}/deactivate`,
  );

  reactivateAgent = this.createPayloadHandler<
    AgentInfoResponse,
    { args: { agencyId: number; agentId: number } }
  >(
    'POST',
    ({ agencyId, agentId }) =>
      `/agency/${agencyId}/agent/${agentId}/reactivate`,
  );

  getSeats = this.createGetHandler<
    SeatsInfoResponse,
    { args: { agencyId: number } }
  >(({ agencyId }) => `/agency/${agencyId}/seats`);

  getSeatsPricing = this.createGetHandler<
    SeatsPricingInfoResponse,
    { args: { agencyId: number } }
  >(({ agencyId }) => `/agency/${agencyId}/seats/pricing`);

  addSeats = this.createPayloadHandler<
    SeatsInfoResponse,
    { args: { agencyId: number }; payload: { quantity?: number } }
  >('POST', ({ agencyId }) => `/agency/${agencyId}/seats/add`);

  removeSeats = this.createPayloadHandler<
    SeatsInfoResponse,
    { args: { agencyId: number } }
  >('POST', ({ agencyId }) => `/agency/${agencyId}/seats/remove`);

  getAgencyFeatureFlag = this.createGetHandler<
    boolean,
    { args: { agencyId: number; featureName: string } }
  >(
    ({ agencyId, featureName }) =>
      `/agency/${agencyId}/feature-flags/${featureName}`,
  );

  createAgent = this.createPayloadHandler<
    AgentInfoResponse,
    { args: { agencyId: number }; payload: AgentInput }
  >('POST', ({ agencyId }) => `/agency/${agencyId}/agent`, {
    isFormData: true,
  });

  updateAgent = this.createPayloadHandler<
    AgentInfoResponse,
    {
      args: { agentId: number; agencyId: number };
      payload: AgentInput;
    }
  >(
    'POST',
    ({ agentId, agencyId }) =>
      `agency/${agencyId}/agent/${agentId}/update-settings`,
    {
      isFormData: true,
    },
  );

  updateAgentSelf = this.createPayloadHandler<
    AgentInfoResponse,
    {
      args: { agentId: number };
      payload: Partial<Omit<AgentInput, 'isAdmin'>> & {
        autoGenerateSlug?: boolean;
      };
    }
  >('POST', ({ agentId }) => `/agent/${agentId}/update-settings`, {
    isFormData: true,
  });

  sendClientEmail = this.createPayloadHandler<
    AgentEmailSentResponse,
    {
      payload: ClientEmailDetails;
    }
  >('POST', () => '/templated-client-notifications/send-email', {
    isFormData: true,
  });

  sendClientSMS = this.createPayloadHandler<
    OwlResponse<Client>,
    {
      payload: ClientSmsDetails & { testClient: boolean };
    }
  >('POST', () => '/templated-client-notifications/send-sms', {
    isFormData: true,
  });

  getTemplateOptions = this.createGetHandler<
    AgentEmailTemplateOptionsResponse,
    { query: { medium: ContactMedium } }
  >(() => `/templated-client-notifications/get-templates`);

  getCustomTemplateList = this.createGetHandler<
    PagedOwlResponse<CustomTemplateDetails[]>,
    {
      query: { page: number; medium: ContactMedium };
    }
  >(() => `/custom-templates`);

  getSpecificTemplatePreview = this.createGetHandler<
    AgentEmailTemplateResponse,
    {
      query: ClientSmsDetails | ClientEmailDetails;
    }
  >(() => `/templated-client-notifications/get-template-preview`);

  getSpecificCustomTemplatePreview = this.createPayloadHandler<
    AgentCustomTemplateResponse,
    {
      payload:
        | CustomSmsTemplatePreviewDetails
        | CustomEmailTemplatePreviewDetails;
    }
  >('POST', () => `/templated-client-notifications/template-preview`);

  createCustomTemplate = this.createPayloadHandler<
    AgentCreateCustomTemplateResponse,
    { payload: CustomTemplate }
  >('POST', () => `/custom-templates`);

  updateCustomTemplate = this.createPayloadHandler<
    OwlResponse<CustomTemplateDetails>,
    {
      args: { templateId: number };
      payload: CustomTemplateDetails;
    }
  >('PATCH', ({ templateId }) => `/custom-templates/${templateId}`);

  deleteCustomTemplate = this.createPayloadHandler<
    OwlResponse<undefined>,
    {
      args: { templateId: number };
    }
  >('DELETE', ({ templateId }) => `/custom-templates/${templateId}`);

  getSpecificCustomTemplateDetails = this.createGetHandler<
    OwlResponse<CustomTemplateDetails>,
    { args: { templateId: number } }
  >(({ templateId }) => `/custom-templates/${templateId}`);

  completeAgentOnboarding = this.createPayloadHandler<
    AgentInfoWithAgencyResponse,
    { args: { id: number } }
  >('POST', ({ id }) => `/agent/${id}/mark-onboarding-complete`);

  getAgentLinkSettings = this.createGetHandler<
    OwlResponse<Record<PublicSetting, any>>,
    Record<string, never>
  >(() => '/settings/agent');

  updateAgentPublicSettings = this.createPayloadHandler<
    OwlResponse<undefined>,
    { payload: AgentPublicSettings[] }
  >('PUT', () => '/settings/agent');

  updateAgencyPublicSettings = this.createPayloadHandler<
    OwlResponse<undefined>,
    { payload: AgentPublicSettings[] }
  >('PUT', () => '/settings/agency');

  validateResetPasswordToken = this.createPayloadHandler<
    OwlResponse<undefined>,
    { payload: { email: string; token: string } }
  >('POST', () => '/auth/validate-reset-password-token');

  getAgentIncentiveInfo = this.createGetHandler<
    AgentIncentiveInfoResponse,
    Record<string, never>
  >(() => '/client-submission-goal/progress');

  getClientSalesFunnelStatuses = this.createGetHandler<
    OwlResponse<Record<keyof SalesFunnelStatus, string>>,
    Record<string, never>
  >('/client/sales-funnel-statuses');

  updateClientSalesFunnelStatus = this.createPayloadHandler<
    SalesFunnelStatusUpdateResponse,
    {
      args: { clientId: string };
      payload: {
        salesFunnelStatus: keyof SalesFunnelStatus;
        actualPremium?: string;
      };
    }
  >('PATCH', ({ clientId }) => `/client/${clientId}/sales-funnel-status`);

  getSubscriptionPricingOptions = this.createGetHandler<
    AgencySubscriptionPricingOptionsResponse,
    Record<never, never>
  >('/subscription/options');

  setSubscriptionPricingOption = this.createPayloadHandler<
    StripePaymentIntentResponse,
    {
      args: { agencyId: number };
      payload: {
        term: RenewalFrequency;
      };
    }
  >('POST', ({ agencyId }) => `/agency/${agencyId}/set-new-subscription-term`);

  sendSMSWithLink = this.createPayloadHandler<
    OwlResponse<any>,
    {
      args: {
        agentId: number;
      };
      payload: {
        mobilePhoneNumber: string;
        insuranceLine?: LineType;
        showAcceleratedUnderwritingQuestions?: boolean;
      };
    }
  >('POST', ({ agentId }) => `/agent/${agentId}/send-sms-with-insurgrid-link`);

  getImpersonableUsers = this.createGetHandler<
    PagedOwlResponse<ImpersonationUser[]>,
    {
      query: {
        /** searches first/last name, email FROM START */
        searchPhrase: string;
        page: number;
      };
    }
  >('/impersonate/search-users', { per_page: 10 });

  startUserImpersonation = this.createPayloadHandler<
    OwlResponse<never>,
    { args: { userId: number } }
  >('POST', ({ userId }) => `/impersonate/start/${userId}`);

  stopUserImpersonation = this.createPayloadHandler<
    OwlResponse<never>,
    Record<string, unknown>
  >('POST', () => `/impersonate/stop`);

  getCustomLinks = this.createGetHandler<
    PagedOwlResponse<CustomLink[]>,
    {
      query: {
        includeArchived?: boolean;
        includeFormReferralPartners?: boolean;
        perPage?: number;
        page?: number;
      };
    }
  >('/custom-links');

  getCustomLinkStats = this.createGetHandler<
    OwlResponse<CustomLinkStats>,
    {
      args: {
        customLinkSlug: string;
        agentId: number;
      };
    }
  >(
    ({ customLinkSlug, agentId }) =>
      `/agent/${agentId}/custom-links/${customLinkSlug}/stats`,
  );

  createCustomLink = this.createPayloadHandler<
    OwlResponse<CustomLink>,
    {
      payload: {
        label: string;
        isFormReferralPartner?: boolean;
      } & ReferralPartnerInput;
    }
  >('POST', () => '/custom-links', { isFormData: true });

  updateCustomLink = this.createPayloadHandler<
    OwlResponse<CustomLink>,
    {
      args: { linkId: string };
      payload: {
        label: string;
      };
    }
  >('PATCH', ({ linkId }) => `/custom-links/${linkId}`);

  updateCustomLinkSettings = this.createPayloadHandler<
    OwlResponse<CustomLink>,
    {
      args: { linkId: string };
      payload: { label?: string } & ReferralPartnerInput;
    }
  >('POST', ({ linkId }) => `/custom-links/${linkId}/update-settings`, {
    isFormData: true,
  });

  updateClientSource = this.createPayloadHandler<
    OwlResponse<Client>,
    {
      args: { clientId: string };
      payload: {
        attributionSource?: string;
        customLinkUuid?: string;
      };
    }
  >('PATCH', ({ clientId }) => `/client/${clientId}/attribution-source`);

  archiveCustomLink = this.createPayloadHandler<
    OwlResponse<CustomLink>,
    { args: { linkId: string } }
  >('POST', ({ linkId }) => `/custom-links/${linkId}/archive`);

  unarchiveCustomLink = this.createPayloadHandler<
    OwlResponse<CustomLink>,
    { args: { linkId: string } }
  >('POST', ({ linkId }) => `/custom-links/${linkId}/unarchive`);

  private handleLogin = this.createPayloadHandler<
    LoginResponse,
    { payload: LoginData }
  >('POST', '/auth/login');

  private handleValidateMagicToken = this.createPayloadHandler<
    LoginResponse,
    {
      query: { token: string; signature: string };
    }
  >('POST', '/auth/validate-magic-link');

  // TODO make authed agent client and move these there

  createHelpRequest = this.createPayloadHandler<
    OwlResponse<{ slackMessageId: string }>,
    { args: { agentId: number } }
  >('POST', ({ agentId }) => `/agent/${agentId}/help-request`);

  submitFeedback = this.createPayloadHandler<
    OwlResponse<{ slackMessageId: string }>,
    {
      args: { agentId: number };
      payload: { slackMessageId: string | null; feedback: string };
    }
  >('POST', ({ agentId }) => `/agent/${agentId}/help-request`);

  sendSlackNotification = this.createPayloadHandler<
    OwlResponse<{ success: boolean }>,
    { payload: { message: string } }
  >('POST', () => '/slack/send-notification');

  updatePolicy = this.createPayloadHandler<
    null,
    {
      args: { policyUUID: string };
      payload: UpdateClientPolicyPayload;
    }
  >('PUT', ({ policyUUID }) => `/policy/${policyUUID}`);

  linkBrowserbaseSession = this.createPayloadHandler<
    OwlResponse<{ success: boolean }>,
    {
      payload: {
        client_submissions_uuid: string;
        browserbase_uuid: string;
      };
    }
  >('POST', () => '/browserbase/session');

  getAuthToken() {
    return this.storage.getItem(ACCESS_TOKEN_KEY);
  }

  async checkIfInsurgridEmployee() {
    if (!this.storage.getItem(ACCESS_TOKEN_KEY)) {
      return false;
    }
    try {
      await this.client.get('/user/insurgrid-admin-check');
      return true;
    } catch {
      return false;
    }
  }

  adminCheck(header: string) {
    return this.client.get('/user/insurgrid-admin-check', {
      headers: { Authorization: header },
    });
  }

  async getCurrentUser() {
    if (!this.storage.getItem(ACCESS_TOKEN_KEY)) {
      return null;
    }
    try {
      return this.fetchCurrentUser();
    } catch {
      return null;
    }
  }

  async login(payload: LoginData) {
    const response = await this.handleLogin({ payload });
    this.storage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
  }

  async validateMagicToken(query: ValidateTokenData) {
    const response = await this.handleValidateMagicToken({ query });
    this.storage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
  }

  async setPasswordAndLogin(payload: SetPasswordDetails) {
    const response = await this.setNewPasswordAndLogin({ payload });
    this.storage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
  }

  async logout() {
    try {
      await this.handleLogout();
    } finally {
      this.storage.removeItem(ACCESS_TOKEN_KEY);
    }
  }

  private handleLogout() {
    return this.client.post<undefined>('/auth/logout');
  }

  private async fetchCurrentUser() {
    const response = await this.client.get<UserResponse>('/user', {
      params: { include: '*' },
    });
    return response.data.data;
  }

  public getPolicyTypes = this.createGetHandler<
    PolicyTypeResponse,
    Record<any, never>
  >('/policy-types');
}
